import React from 'react';
import { 
  X, Home, Briefcase, Package, Info, Mail, FolderOpen, LogIn, User,
  Settings, Users, MessageSquare, ShoppingCart, FolderKanban, History, 
  Building2, Gift, FileText, Calculator
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  // Menu items communs (publics)
  const publicMenuItems = [
    { icon: Home, label: 'Accueil', path: '/' },
    { icon: Briefcase, label: 'Services', path: '/services' },
    { icon: Package, label: 'Packs', path: '/packs' },
    { icon: Info, label: 'À propos', path: '/aboutirissoftech' },
    { icon: Mail, label: 'Contact', path: '/contact' },
  ];

  // Menu items pour Admin
  const adminMenuItems = [
    { icon: Settings, label: 'Dashboard Admin', path: '/admin' },
    { icon: Users, label: 'Utilisateurs', path: '/users' },
    { icon: MessageSquare, label: 'Messages', path: '/messages' },
    { icon: ShoppingCart, label: 'Demandes Clients', path: '/demandesclients' },
    { icon: FolderKanban, label: 'Gestion Projets', path: '/gestionprojets' },
    { icon: History, label: 'Historique', path: '/historique' },
    { icon: Building2, label: 'Profil Entreprise', path: '/company-profile' },
  ];

  // Menu items pour Utilisateur standard
  const userMenuItems = [
    { icon: Calculator, label: 'Demander un Devis', path: '/devisrequest' },
    { icon: User, label: 'Mon Profil', path: '/profile' },
    { icon: Gift, label: 'Mes Promos', path: '/mespromos' },
    { icon: FileText, label: 'Mes Factures', path: '/factures' },
    { icon: FolderOpen, label: 'Mes Projets', path: '/projets' },
  ];

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Menu Slide-in */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-gradient-to-br from-white to-gray-50 shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
            Menu
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* User Info (si connecté) */}
        {isAuthenticated && user && (
          <div className="p-6 bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.username}
                </p>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Items - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-4">
          {/* Menu public */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-2">
              Navigation
            </h3>
            <ul className="space-y-1">
              {publicMenuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.label}>
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className="w-full flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-gradient-to-r hover:from-amber-50 hover:to-amber-100/50 transition-all duration-200 group"
                    >
                      <Icon className="w-5 h-5 text-gray-600 group-hover:text-amber-600 transition-colors" />
                      <span className="text-gray-700 group-hover:text-amber-900 font-medium">
                        {item.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Menu spécifique utilisateur connecté */}
          {isAuthenticated && user && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-2">
                {user.isAdmin ? 'Administration' : 'Mon Espace'}
              </h3>
              <ul className="space-y-1">
                {(user.isAdmin ? adminMenuItems : userMenuItems).map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.label}>
                      <button
                        onClick={() => handleNavigation(item.path)}
                        className="w-full flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-gradient-to-r hover:from-amber-50 hover:to-amber-100/50 transition-all duration-200 group"
                      >
                        <Icon className="w-5 h-5 text-gray-600 group-hover:text-amber-600 transition-colors" />
                        <span className="text-gray-700 group-hover:text-amber-900 font-medium">
                          {item.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </nav>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
          {isAuthenticated ? (
            <button
              onClick={handleLogout}
              className="w-full py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-200"
            >
              Se déconnecter
            </button>
          ) : (
            <button
              onClick={() => handleNavigation('/login')}
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2"
            >
              <LogIn className="w-5 h-5" />
              Se connecter
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
